import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Crosshair, Zap, CheckCircle2, XCircle,
  Palette, LayoutTemplate, Server, CreditCard, Mail, UserRound, Cog,
} from "lucide-react";
import { fetchSettings, saveSettings, testRegistrar, fetchStatus, api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import Sidebar from "@/components/dashboard/Sidebar";
import LoginAttemptsFeed from "@/components/settings/LoginAttemptsFeed";
import AvatarDropzone from "@/components/settings/AvatarDropzone";
import CoverImageUpload from "@/components/marketplace/CoverImageUpload";
import DynadotWebhookPanel from "@/components/settings/DynadotWebhookPanel";
import SnitcherStatus from "@/components/settings/SnitcherStatus";
import SeoIndexingPanel from "@/components/settings/SeoIndexingPanel";
import LiveModePanel from "@/components/settings/LiveModePanel";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";

const SECRET_KEYS = new Set([
  "TRANSIP_BEARER_TOKEN", "TRANSIP_PRIVATE_KEY",
  "SPACESHIP_API_KEY", "SPACESHIP_API_SECRET",
  "DYNADOT_API_KEY", "DYNADOT_SECRET", "DYNADOT_WEBHOOK_SECRET",
  "MAILGUN_API_KEY",
  "STRIPE_SECRET_KEY", "BUNQ_API_KEY",
  "IPINFO_TOKEN", "IPSTACK_TOKEN", "GOOGLE_API", "WHOISJSON_TOKEN",
  "GOOGLE_INDEXING_SA_JSON",
]);

const CATEGORIES = [
  { id: "branding",       label: "Branding",       icon: Palette,        hint: "Names, logos, avatars, and copy shown across the public app." },
  { id: "homepage",       label: "Homepage",       icon: LayoutTemplate, hint: "The public homepage hero — headline, stats, background media." },
  { id: "registrars",     label: "Registrars",     icon: Server,         hint: "Domain registrar API keys, mode, and per-provider webhooks." },
  { id: "payments",       label: "Payments",       icon: CreditCard,     hint: "Stripe checkout + Bunq payout credentials." },
  { id: "communications", label: "Communications", icon: Mail,           hint: "Email, hot-lead alerts, visitor identification, and inbox templates." },
  { id: "contact",        label: "Contact & compliance", icon: UserRound, hint: "Registrant details required by TLD registries + enrichment tokens." },
  { id: "system",         label: "System",         icon: Cog,            hint: "Timezone + lander defaults + admin activity feed." },
];

const SECTIONS = [
  {
    category: "branding",
    title: "General",
    hint: "Branding shown across the app (sidebar, headers, login page, lander footer).",
    fields: [
      { key: "BRAND_NAME", label: "Brand name" },
      { key: "LOGIN_OVERLINE", label: "Login overline (small caps text above headline)" },
      { key: "LOGIN_HEADLINE", label: "Login page headline" },
      { key: "LOGIN_SUBTEXT", label: "Login page subtext", type: "textarea" },
    ],
  },
  {
    category: "homepage",
    title: "Homepage hero",
    hint: 'Controls the big navy hero on the public homepage. Leave any field blank to keep the built-in defaults. The rotating word cycles through the comma-separated list every ~3 seconds.',
    fields: [
      { key: "HERO_HEADLINE_PREFIX", label: 'Prefix (before the rotating word) — e.g. "The"' },
      { key: "HERO_ROTATING_WORDS", label: "Rotating words (comma-separated)", type: "textarea" },
      { key: "HERO_HEADLINE_SUFFIX", label: 'Suffix (after the rotating word) — e.g. "lives at the perfect domain."' },
      { key: "HERO_SUBHEADING", label: "Subheading paragraph", type: "textarea" },
      { key: "HERO_MEDIA_URL", label: "Background media URL (image .jpg/.png/.webp or video .mp4/.webm) — leave blank to keep the aurora blobs" },
      { key: "HERO_STAT_1_VALUE", label: 'Stat 1 value — e.g. "1,000" (blank = auto from live inventory)' },
      { key: "HERO_STAT_1_LABEL", label: 'Stat 1 label — e.g. "Live inventory"' },
      { key: "HERO_STAT_2_VALUE", label: 'Stat 2 value — e.g. "€2.4M" (blank = auto from moved-to-date)' },
      { key: "HERO_STAT_2_LABEL", label: 'Stat 2 label — e.g. "Moved to date"' },
      { key: "HERO_STAT_3_VALUE", label: 'Stat 3 value — e.g. "< 24h"' },
      { key: "HERO_STAT_3_LABEL", label: 'Stat 3 label — e.g. "Avg transfer"' },
      { key: "REGISTER_DOMAIN_URL", label: 'Header "Register your domain" URL — e.g. https://www.spaceship.com/domains/?ref=your-affiliate-id' },
    ],
  },
  {
    category: "registrars",
    title: "TransIP.nl",
    registrar: "transip",
    hint: "Provide either a Bearer JWT (pasted from api call, ~14d valid) OR login + PEM private key for auto-refresh.",
    fields: [
      { key: "TRANSIP_BEARER_TOKEN", label: "Bearer JWT", type: "textarea" },
      { key: "TRANSIP_LOGIN", label: "Login name" },
      { key: "TRANSIP_PRIVATE_KEY", label: "Private key (PEM)", type: "textarea" },
    ],
  },
  {
    category: "registrars",
    title: "Spaceship.com",
    registrar: "spaceship",
    hint: "Create keys at https://www.spaceship.com/application/api-manager/",
    fields: [
      { key: "SPACESHIP_API_KEY", label: "API key" },
      { key: "SPACESHIP_API_SECRET", label: "API secret" },
    ],
  },
  {
    category: "registrars",
    title: "Dynadot",
    registrar: "dynadot",
    hint:
      "Live registration + webhooks. Set an API key in Dynadot → Tools → API. Whitelist this pod's egress IP under Tools → API Settings → Allowed IPs (see 'Outbound IP' below).",
    fields: [
      { key: "DYNADOT_API_KEY", label: "API key" },
      { key: "DYNADOT_SECRET", label: "API secret (optional — used to HMAC-sign outbound webhooks)" },
      { key: "DYNADOT_ACCOUNT_ID", label: "Account username (v2 REST API; optional)" },
      { key: "DYNADOT_CONTACT_ID", label: "Contact profile ID (numeric; from Dynadot → Contact Profiles). Falls back to 1." },
      { key: "DYNADOT_WEBHOOK_URL", label: "Outbound webhook URL (POSTed on snipe.won and listing.sold)" },
      { key: "DYNADOT_WEBHOOK_SECRET", label: "Webhook secret (URL path token for inbound + HMAC secret for outbound)", type: "webhook_secret" },
    ],
  },
  {
    category: "registrars",
    title: "Hosting.nl",
    registrar: "hostingnl",
    hint: "Create an API key at Mijn Hosting.NL → Beheer API. Best-effort integration; iterate on real errors.",
    fields: [
      { key: "HOSTINGNL_API_KEY", label: "API key (Bearer token)" },
    ],
  },
  {
    category: "registrars",
    title: "Realtime Register",
    registrar: "realtimeregister",
    hint: "YourSRS v2 REST — https://dm.realtimeregister.com/docs/api/. Create an API token at your customer portal. Use the OTE endpoint for sandbox tests.",
    fields: [
      { key: "REALTIMEREGISTER_API_KEY", label: "API token (Bearer)", type: "password" },
      { key: "REALTIMEREGISTER_CUSTOMER", label: "Customer handle (blank for the token owner)" },
      { key: "REALTIMEREGISTER_USE_OTE", label: "Use OTE / sandbox endpoint", type: "select", options: ["false", "true"] },
    ],
  },
  {
    category: "registrars",
    title: "Openprovider",
    registrar: "openprovider",
    hint: "v1beta REST — https://docs.openprovider.com. Uses username + password (JWT auto-fetched on first call, cached in-memory).",
    fields: [
      { key: "OPENPROVIDER_USERNAME", label: "Username" },
      { key: "OPENPROVIDER_PASSWORD", label: "Password", type: "password" },
    ],
  },
  {
    category: "registrars",
    title: "SIDN (.nl registry — lookup only)",
    registrar: "sidn",
    hint: "SIDN accepts direct registration ONLY from EPP-accredited tag holders. For scouting, this client exposes availability / expiring-domain lookups; pair it with TransIP / Realtime Register / Openprovider for the actual register call.",
    fields: [
      { key: "SIDN_ENABLED", label: "Enabled", type: "select", options: ["true", "false"] },
      { key: "SIDN_DROPLIST_URL", label: "Drop-list JSON URL (optional — for expiring-.nl ingest)" },
    ],
  },
  {
    category: "registrars",
    title: "CentralNic Reseller (rrpproxy.net)",
    registrar: "cnr",
    sandbox: { toggleKey: "CNR_USE_OTE", label: "Register test .com in OT&E" },
    hint: "REST v1 client for CentralNic Reseller (RRPproxy). Uses HTTP Basic auth. Enable OT&E while testing sandbox flows, then use the sandbox-register button below to verify the credentials end-to-end before flipping live.",
    fields: [
      { key: "CNR_LOGIN", label: "Reseller login" },
      { key: "CNR_PASSWORD", label: "Password", type: "password" },
      { key: "CNR_USE_OTE", label: "Use OT&E / sandbox endpoint", type: "select", options: ["false", "true"] },
      { key: "CNR_CONTACT_HANDLE", label: "Contact handle (P-XXX from rrpproxy.net → Contacts — required for sandbox register)" },
    ],
  },
  {
    category: "registrars",
    title: "AutoDNS (autodns.com by InterNetX)",
    registrar: "autodns",
    sandbox: { toggleKey: "AUTODNS_USE_DEMO", label: "Register test .com in demo" },
    hint: "InterNetX AutoDNS JSON REST v1. HTTP Basic auth + optional customer context (used as X-Domainrobot-Context). Enable demo mode + set a contact handle, then run the sandbox-register button to verify end-to-end.",
    fields: [
      { key: "AUTODNS_USER", label: "AutoDNS user" },
      { key: "AUTODNS_PASSWORD", label: "Password", type: "password" },
      { key: "AUTODNS_CONTEXT", label: "Customer context (blank = user's default)" },
      { key: "AUTODNS_USE_DEMO", label: "Use demo endpoint", type: "select", options: ["false", "true"] },
      { key: "AUTODNS_CONTACT_HANDLE", label: "Contact handle (contact ID from autodns.com → Contacts — required for sandbox register)" },
    ],
  },
  {
    category: "communications",
    title: "Mailgun (offer notifications)",
    hint: "Used to email offer/purchase notifications to NOTIFY_EMAIL.",
    fields: [
      { key: "MAILGUN_API_KEY", label: "API key" },
      { key: "MAILGUN_DOMAIN", label: "Sending domain (e.g. mg.yourdomain.com)" },
      { key: "MAILGUN_REGION", label: "Region", type: "select", options: ["us", "eu"] },
      { key: "MAILGUN_FROM", label: "From address" },
      { key: "NOTIFY_EMAIL", label: "Notify recipient email" },
    ],
  },
  {
    category: "communications",
    title: "Hot lead alerts",
    hint: "Fire a push email the moment a single visitor crosses X views on one domain — the buyer is on the page RIGHT NOW.",
    fields: [
      { key: "PEAK_INTENT_THRESHOLD", label: "Peak-intent threshold (default 5)" },
      { key: "PEAK_INTENT_ALERT_EMAIL", label: "Alert recipient (leave blank to fall back to NOTIFY_EMAIL)" },
    ],
  },
  {
    category: "system",
    title: "Lander defaults",
    hint: "Public for-sale pages served at /l/{domain}. Set price per-listing on the Listings page.",
    fields: [
      { key: "LANDER_CURRENCY", label: "Currency (EUR/USD/GBP)" },
    ],
  },
  {
    category: "payments",
    title: "Stripe (checkout — v2)",
    hint: "Stored securely. Actual Stripe checkout wiring on lander is deferred to next iteration.",
    fields: [
      { key: "STRIPE_PUBLIC_KEY", label: "Public key (pk_…)" },
      { key: "STRIPE_SECRET_KEY", label: "Secret key (sk_…)" },
    ],
  },
  {
    category: "payments",
    title: "Bunq (payout — v2)",
    hint: "Stored securely. Automatic payout integration is deferred to next iteration.",
    fields: [
      { key: "BUNQ_API_KEY", label: "API key" },
      { key: "BUNQ_ENV", label: "Environment", type: "select", options: ["sandbox", "production"] },
      { key: "BUNQ_USER_ID", label: "User ID" },
      { key: "BUNQ_MONETARY_ACCOUNT_ID", label: "Monetary account ID" },
    ],
  },
  {
    category: "system",
    title: "Go-live readiness",
    liveMode: true,
    hint:
      "There is no simulation mode — every snipe fires a real TransIP registration. This checklist shows what still blocks real registrations (expired token, incomplete contact profile). Use “Verify credentials” for a read-only probe: it authenticates and runs one availability lookup, nothing is registered. SIDN rejects .nl registrations without a complete contact profile.",
    fields: [],
  },
  {
    category: "system",
    title: "Drop alerts",
    hint:
      "Email heads-up before an armed snipe fires, plus a won/lost email the moment it resolves. Sent through your existing Mailgun setup.",
    fields: [
      { key: "DROP_ALERT_ENABLED", label: "Alerts live", type: "select", options: ["true", "false"] },
      { key: "DROP_ALERT_LEAD_MIN", label: "Minutes before the drop (default 60)" },
      { key: "ALERT_EMAIL", label: "Send alerts to (defaults to the notify address)" },
    ],
  },
  {
    category: "system",
    title: "Share nudge · referral discount",
    hint:
      "Visitors who share a lander can trade their email for a single-use discount code (emailed to them, and to you as a lead). Codes are bound to one domain, expire automatically, and are applied server-side at checkout. Only offered on listings priced at 3× the discount or more.",
    fields: [
      { key: "SHARE_DISCOUNT_ENABLED", label: "Share offer live", type: "select", options: ["true", "false"] },
      { key: "SHARE_DISCOUNT_AMOUNT", label: "Flat amount off (listing currency, e.g. 100)" },
      { key: "SHARE_DISCOUNT_DAYS", label: "Code valid for (days)" },
    ],
  },
  {
    category: "system",
    title: "SEO · indexing & social cards",
    seo: true,
    hint:
      "Canonical public origin used in sitemaps, OG cards and search-engine pings. New and re-priced landers are pushed to IndexNow (Bing / Yandex / Naver) automatically — Google's sitemap ping was retired, so add a service-account JSON below to also use the Google Indexing API (Google Cloud → APIs → Indexing API → service account, then add that service account as an owner of your Search Console property).",
    fields: [
      { key: "PUBLIC_BASE_URL", label: "Public base URL (e.g. https://app.casperdomains.com)" },
      { key: "SEO_AUTO_PING", label: "Auto-submit on listing changes", type: "select", options: ["true", "false"] },
      { key: "GOOGLE_INDEXING_SA_JSON", label: "Google Indexing API service-account JSON", type: "textarea", rows: 8 },
    ],
  },
  {
    category: "contact",
    title: "Analytics enrichment (v2)",
    hint: "Stored for future IP geo enrichment on tracking events.",
    fields: [
      { key: "IPINFO_TOKEN", label: "IPInfo token" },
      { key: "IPSTACK_TOKEN", label: "IPStack token" },
      { key: "GOOGLE_API", label: "Google API key" },
      { key: "WHOISJSON_TOKEN", label: "WhoisJson.com token (powers WHOIS on domain check)" },
    ],
  },
  {
    category: "system",
    title: "Display timezone",
    hint: "All UTC timestamps in the dashboard (Snipes, Drops, Portfolio) render in this timezone. Leave blank to use your browser's timezone.",
    fields: [
      { key: "USER_TIMEZONE", label: "IANA timezone (e.g. Europe/Amsterdam, America/New_York, UTC)" },
    ],
  },
  {
    category: "branding",
    title: "Public site · brand assets",
    hint: "Logo image + circular portrait shown on the public homepage + About page, and the Spaceship Marketplace reseller token used to build every Buy-on-Spaceship link.",
    fields: [
      { key: "BRANDS_LOGO_URL", label: "CasperBrands&Co logo URL (login panel)" },
      { key: "LOGO_URL", label: "Logo URL (SVG/PNG, transparent, ~200×48 renders best in the header)" },
      { key: "AVATAR_URL", label: "Portrait URL (square PNG/JPG ≥ 200×200 recommended)" },
      { key: "SPACESHIP_MARKETPLACE_TOKEN", label: "Spaceship marketplace reseller token" },
    ],
  },
  {
    category: "communications",
    title: "Snitcher · visitor identification",
    snitcher: true,
    hint:
      "Snitcher identifies which companies visit your marketplace. Paste the full snippet from Snitcher → Settings → Installation into the first field — it will be injected on the homepage + all landers automatically. The optional email link tag is appended as a query string to every link in your outbound cold-email HTML (e.g. `?snitcher_src=cold_email`) so click-throughs are attributed cleanly in Snitcher's dashboard.",
    fields: [
      { key: "SNITCHER_SNIPPET", label: "Snitcher tracking snippet (full <script>… block)", type: "textarea", rows: 8 },
      { key: "SNITCHER_EMAIL_LINK_TAG", label: "Optional: query string for outbound-email links (blank = no tagging)" },
    ],
  },
  {
    category: "communications",
    title: "Bullseye.so · visitor tracking",
    hint:
      'Bullseye identifies U.S. company visitors. Paste your **Pixel API key** (Bullseye → Organization Settings → API Keys) below to inject the tracking script. Choose which public pages the pixel runs on (comma-separated: home, lander, sold, projects, about). Leave the pages field blank to run on every public page.',
    fields: [
      { key: "BULLSEYE_API_KEY", label: "Bullseye pixel API key" },
      { key: "BULLSEYE_PAGES", label: "Pages to run on (comma-separated) — e.g. home,lander,sold" },
    ],
  },
  {
    category: "communications",
    title: "Bullseye.so · identified companies feed",
    hint:
      'Optional — paste your **Org API key** (`bsk_live_…` from Bullseye → API Keys with the `read` scope) and your Organization ID to surface identified U.S. companies inside the Hot Leads page. The two Bullseye keys are different: the pixel key above powers tracking, the org API key below reads the results back into the dashboard.',
    fields: [
      { key: "BULLSEYE_DATA_API_KEY", label: "Bullseye org API key (bsk_live_…)" },
      { key: "BULLSEYE_ORG_ID", label: "Bullseye organization ID" },
    ],
  },
  {
    category: "communications",
    title: "Inbox · reply templates",
    hint:
      'Three quick-reply presets shown in the Inbox composer. Paste a JSON array of `{key, label, color, subject, body}` objects — the built-in Accept / Counter / Reject presets are used when this field is blank. Body supports these placeholders: `{name}`, `{domain}`, `{amount}`, `{counter}`, `{subject}`, `{broker}`. Colors accept any CSS value (e.g. `#12b76a`).',
    fields: [
      { key: "REPLY_TEMPLATES_JSON", label: "Reply templates JSON", type: "textarea", rows: 14 },
    ],
  },
  {
    category: "branding",
    title: "About page · your story",
    hint:
      "Full body of the /about page in lightweight markdown. Anything before the first `## Section` is the italic lede. Each `## Section Title` starts a new titled block. Supports **bold**, _italic_, and [label](https://url) links. Blank line = new paragraph. Leave empty to fall back to the default sample story.",
    fields: [
      { key: "ABOUT_STORY_MD", label: "About page markdown", type: "textarea", rows: 18 },
    ],
  },
  {
    category: "contact",
    title: "Registrant contact",
    hint: "Used as registrant on TransIP and all four roles on Spaceship.",
    fields: [
      { key: "CONTACT_FIRST_NAME", label: "First name" },
      { key: "CONTACT_LAST_NAME", label: "Last name" },
      { key: "CONTACT_COMPANY", label: "Company (optional)" },
      { key: "CONTACT_COMPANY_KVK", label: "KVK number (required by TransIP for .nl when Company is set)" },
      { key: "CONTACT_COMPANY_TYPE", label: "Company type (required by TransIP for .nl — e.g. BV, VOF, EENMANSZAAK, ANDERS)" },
      { key: "CONTACT_COMPANY_VAT", label: "VAT number (optional, e.g. NL123456789B01)" },
      { key: "CONTACT_EMAIL", label: "Email" },
      { key: "CONTACT_PHONE", label: "Phone (E.164, e.g. +31612345678)" },
      { key: "CONTACT_STREET", label: "Street" },
      { key: "CONTACT_NUMBER", label: "House number" },
      { key: "CONTACT_CITY", label: "City" },
      { key: "CONTACT_ZIP", label: "ZIP / Postal code" },
      { key: "CONTACT_COUNTRY", label: "Country (ISO-2, e.g. NL)" },
      { key: "CONTACT_FAX", label: "Fax number (optional — TransIP requires the field even if blank)" },
    ],
  },
];

export default function Settings() {
  const brand = useBranding();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("cat") || CATEGORIES[0].id;
  const [settings, setSettings] = useState(null);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState({});
  const [testResult, setTestResult] = useState({});
  const [status, setStatus] = useState({});

  const visibleSections = useMemo(
    () => SECTIONS.filter((s) => (s.category || "system") === activeCategory),
    [activeCategory],
  );
  const activeCat = useMemo(
    () => CATEGORIES.find((c) => c.id === activeCategory) || CATEGORIES[0],
    [activeCategory],
  );

  // Per-category "empty setting" count — used to badge tabs that still
  // need attention. Considers a field configured when the current form
  // value OR the persisted stored value is non-empty.
  const categoryEmptyCounts = useMemo(() => {
    if (!settings) return {};
    const counts = {};
    for (const cat of CATEGORIES) counts[cat.id] = 0;
    for (const section of SECTIONS) {
      const catId = section.category || "system";
      for (const f of section.fields || []) {
        const inMem = (values[f.key] ?? "").toString().trim();
        const stored = settings[f.key];
        const hasStored = stored && (stored.set || (stored.value != null && String(stored.value).trim() !== ""));
        if (!inMem && !hasStored) counts[catId] = (counts[catId] || 0) + 1;
      }
    }
    return counts;
  }, [settings, values]);

  const selectCategory = (id) => {
    setSearchParams({ cat: id }, { replace: true });
  };

  const load = async () => {
    try {
      const [s, st] = await Promise.all([fetchSettings(), fetchStatus().catch(() => ({}))]);
      setSettings(s);
      setStatus(st);
      // Prefill non-secret values so user can edit; secret fields stay blank.
      const initial = {};
      for (const [k, v] of Object.entries(s)) {
        if (k === "_source") continue;
        if (!SECRET_KEYS.has(k) && v?.value != null) initial[k] = v.value;
      }
      setValues(initial);
    } catch {
      toast.error("Failed to load settings");
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    // Only send fields the user actually filled in this session.
    const payload = {};
    for (const [k, v] of Object.entries(values)) {
      if (v !== undefined && v !== null && String(v).length > 0) payload[k] = v;
    }
    if (Object.keys(payload).length === 0) {
      toast.warning("Nothing to save");
      return;
    }
    setSaving(true);
    try {
      await saveSettings(payload);
      toast.success("Settings saved");
      setValues({});
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const clearKey = async (k) => {
    setSaving(true);
    try {
      await saveSettings({ [k]: "" });
      toast.warning(`Cleared ${k}`);
      await load();
    } catch {
      toast.error("Clear failed");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (registrar) => {
    setTesting((t) => ({ ...t, [registrar]: true }));
    setTestResult((r) => ({ ...r, [registrar]: null }));
    try {
      const res = await testRegistrar(registrar);
      setTestResult((r) => ({ ...r, [registrar]: res }));
      if (res.ok) toast.success(res.message);
      else toast.error(res.message);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Test failed";
      setTestResult((r) => ({ ...r, [registrar]: { ok: false, message: msg } }));
      toast.error(msg);
    } finally {
      setTesting((t) => ({ ...t, [registrar]: false }));
    }
  };

  const runSandboxRegister = async (registrar) => {
    const key = `${registrar}-sandbox`;
    setTesting((t) => ({ ...t, [key]: true }));
    setTestResult((r) => ({ ...r, [registrar]: null }));
    try {
      const res = await api.post(`/settings/sandbox-register/${registrar}`).then((r) => r.data);
      const msg = res.domain ? `${res.message} — test domain: ${res.domain}` : res.message;
      setTestResult((r) => ({ ...r, [registrar]: { ok: res.ok, message: msg } }));
      if (res.ok) toast.success(msg);
      else toast.error(msg);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Sandbox register failed";
      setTestResult((r) => ({ ...r, [registrar]: { ok: false, message: msg } }));
      toast.error(msg);
    } finally {
      setTesting((t) => ({ ...t, [key]: false }));
    }
  };

  const badge = (k) => {
    const field = settings?.[k];
    if (!field) return null;
    const src = settings._source?.[k] || "unset";
    if (!field.set) return { text: "UNSET", bg: "#FFFBEB", fg: "#B45309" };
    if (src === "db") return { text: "SAVED", bg: "#ECFDF5", fg: "#047857" };
    return { text: "FROM ENV", bg: "#EFF6FF", fg: "#2563EB" };
  };

  return (
    <div
      className="min-h-screen w-full flex bg-white"
      data-testid="settings-page"
    >
      <Sidebar status={status} />
      <main className="flex-1 flex flex-col border-l border-[#eaecf0] min-w-0">
        <header className="h-14 border-b border-[#eaecf0] px-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="overline">{brand.brand_name}</span>
            <span className="text-[#D4D4D8]">/</span>
            <span className="text-sm font-medium">Settings</span>
          </div>
          <Link
            to="/"
            data-testid="back-to-dashboard"
            className="flex items-center gap-2 text-xs text-[#525252] hover:text-[#101828] font-mono uppercase tracking-widest"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Dashboard
          </Link>
        </header>

        {!settings ? (
          <div className="flex-1 flex items-center justify-center font-mono text-sm text-[#525252]" data-testid="settings-loading">
            Loading settings…
          </div>
        ) : (
          <div className="flex-1 flex min-h-0" data-testid="settings-body">
            {/* ---------- Category rail (sub-menu) ---------- */}
            <nav
              aria-label="Settings categories"
              data-testid="settings-category-rail"
              className="w-56 border-r border-[#eaecf0] py-6 px-3 shrink-0 bg-[#FAFAFA] overflow-y-auto"
            >
              <div className="overline px-3 mb-3">Categories</div>
              <ul className="space-y-1">
                {CATEGORIES.map((c) => {
                  const Icon = c.icon;
                  const isActive = c.id === activeCategory;
                  const empty = categoryEmptyCounts[c.id] || 0;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => selectCategory(c.id)}
                        data-testid={`cat-${c.id}`}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-left transition-colors ${isActive ? "bg-[#03142f] text-white" : "hover:bg-[#F4F4F5] text-[#101828]"}`}
                        aria-current={isActive ? "page" : undefined}
                      >
                        <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5} />
                        <span className="truncate flex-1">{c.label}</span>
                        {empty > 0 && (
                          <span
                            data-testid={`cat-${c.id}-badge`}
                            title={`${empty} field${empty > 1 ? "s" : ""} still empty`}
                            className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${isActive ? "bg-white/20 text-white" : "bg-[#FEF0C7] text-[#B54708]"}`}
                          >
                            {empty}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* ---------- Active category form ---------- */}
            <div className="flex-1 overflow-y-auto">
              <form onSubmit={submit} className="max-w-3xl mx-auto px-8 py-10 space-y-10 w-full">
                <div>
                  <h2 className="font-display text-2xl font-medium" data-testid="settings-active-title">{activeCat.label}</h2>
                  {activeCat.hint && (
                    <p className="text-sm text-[#525252] mt-1 max-w-2xl">{activeCat.hint}</p>
                  )}
                </div>
                {visibleSections.length === 0 && (
                  <div className="text-sm text-[#525252] font-mono">No settings in this category.</div>
                )}
                {visibleSections.map((section) => (
          <section key={section.title} className="border border-[#eaecf0]">
            <div className="px-6 py-4 border-b border-[#eaecf0] bg-[#FAFAFA] flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="overline">{section.title.split(" ")[0]}</div>
                <div className="font-display text-lg font-medium">{section.title}</div>
                {section.hint && (
                  <p className="text-xs text-[#525252] mt-1 font-mono">{section.hint}</p>
                )}
                {section.registrar && testResult[section.registrar] && (
                  <div
                    className="mt-2 text-xs font-mono flex items-start gap-1.5"
                    data-testid={`test-result-${section.registrar}`}
                    style={{ color: testResult[section.registrar].ok ? "#047857" : "#BE123C" }}
                  >
                    {testResult[section.registrar].ok ? (
                      <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.5} />
                    ) : (
                      <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.5} />
                    )}
                    <span className="break-all">{testResult[section.registrar].message}</span>
                  </div>
                )}
              </div>
              {section.registrar && (
                <div className="flex flex-col gap-1.5 shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => runTest(section.registrar)}
                    disabled={testing[section.registrar]}
                    data-testid={`test-${section.registrar}-btn`}
                    className="rounded-sm"
                  >
                    <Zap className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                    {testing[section.registrar] ? "Testing…" : "Test connection"}
                  </Button>
                  {section.sandbox && (() => {
                    const enabled = (values[section.sandbox.toggleKey] || settings[section.sandbox.toggleKey]?.value || "").toString().toLowerCase() === "true";
                    return (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => runSandboxRegister(section.registrar)}
                        disabled={!enabled || testing[`${section.registrar}-sandbox`]}
                        data-testid={`sandbox-${section.registrar}-btn`}
                        title={enabled ? "" : `Enable ${section.sandbox.toggleKey} first`}
                        className="rounded-sm text-xs"
                      >
                        <Crosshair className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />
                        {testing[`${section.registrar}-sandbox`] ? "Registering…" : section.sandbox.label}
                      </Button>
                    );
                  })()}
                </div>
              )}
            </div>
            <div className="p-6 space-y-4">
              {section.fields.map((f) => {
                const b = badge(f.key);
                const isSecret = SECRET_KEYS.has(f.key);
                const stored = settings[f.key];
                return (
                  <div key={f.key} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <Label htmlFor={f.key} className="text-sm font-medium">
                        {f.label}
                      </Label>
                      <div className="flex items-center gap-2">
                        {b && (
                          <span
                            data-testid={`badge-${f.key}`}
                            className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm"
                            style={{ background: b.bg, color: b.fg }}
                          >
                            {b.text}
                          </span>
                        )}
                        {stored?.set && (
                          <button
                            type="button"
                            onClick={() => clearKey(f.key)}
                            data-testid={`clear-${f.key}`}
                            className="text-[10px] font-mono text-[#BE123C] hover:underline"
                          >
                            clear
                          </button>
                        )}
                      </div>
                    </div>
                    {isSecret && stored?.masked && (
                      <div className="text-[11px] font-mono text-[#A1A1AA]">
                        currently: {stored.masked}
                      </div>
                    )}
                    {f.type === "select" ? (
                      <Select
                        value={
                          (values[f.key] !== undefined && values[f.key] !== ""
                            ? values[f.key]
                            : (stored?.value || f.options[0]))
                        }
                        onValueChange={(v) => set(f.key, v)}
                      >
                        <SelectTrigger
                          data-testid={`field-${f.key}`}
                          className="rounded-sm font-mono text-sm"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-sm">
                          {f.options.map((o) => (
                            <SelectItem key={o} value={o} data-testid={`opt-${f.key}-${o}`}>
                              {o}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : f.key === "AVATAR_URL" ? (
                      <AvatarDropzone
                        current={values[f.key] ?? stored?.value ?? ""}
                        onChange={async (url) => {
                          set(f.key, url);
                          try {
                            await api.put("/settings", { [f.key]: url });
                            load();
                          } catch { /* silent */ }
                        }}
                      />
                    ) : f.key === "LOGO_URL" ? (
                      <CoverImageUpload
                        value={values[f.key] ?? stored?.value ?? ""}
                        onChange={async (url) => {
                          set(f.key, url);
                          try {
                            await api.put("/settings", { [f.key]: url });
                            load();
                          } catch { /* silent */ }
                        }}
                        endpoint="/upload/logo"
                        accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml"
                        maxMb={2}
                        aspectRatio="4 / 1"
                        labelIdle="Drop your logo here, or"
                        labelDone="Logo uploaded"
                      />
                    ) : f.type === "textarea" ? (
                      <Textarea
                        id={f.key}
                        data-testid={`field-${f.key}`}
                        rows={f.rows || (f.key === "TRANSIP_PRIVATE_KEY" ? 6 : 3)}
                        placeholder={isSecret && stored?.set ? "•••• (paste to replace)" : ""}
                        value={values[f.key] ?? ""}
                        onChange={(e) => set(f.key, e.target.value)}
                        className="rounded-sm font-mono text-xs"
                      />
                    ) : (
                      <Input
                        id={f.key}
                        data-testid={`field-${f.key}`}
                        type={isSecret ? "password" : "text"}
                        placeholder={isSecret && stored?.set ? "•••• (type to replace)" : ""}
                        value={values[f.key] ?? ""}
                        onChange={(e) => set(f.key, e.target.value)}
                        className="rounded-sm font-mono text-sm"
                      />
                    )}
                  </div>
                );
              })}
              {section.registrar === "dynadot" && (
                <DynadotWebhookPanel settings={settings} reload={load} />
              )}
              {section.snitcher && (
                <SnitcherStatus settings={settings} brand={brand} />
              )}
              {section.seo && <SeoIndexingPanel />}
              {section.liveMode && <LiveModePanel />}
            </div>
          </section>
        ))}

        <div className="sticky bottom-4 flex justify-end">
          <Button
            type="submit"
            disabled={saving}
            data-testid="save-settings-btn"
            className="rounded-sm bg-[#03142f] hover:bg-[#0b274d] text-white px-6"
          >
            <Save className="w-4 h-4 mr-2" strokeWidth={1.5} />
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
              </form>

              {activeCategory === "system" && (
                <div className="max-w-3xl mx-auto px-8 pb-16 w-full">
                  <LoginAttemptsFeed />
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
